import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { SelectOption } from "../../../../../core/models/select-option.model";
import { UntypedFormGroup } from "@angular/forms";
import { MatSelect } from "@angular/material/select";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";

@Component({
  selector: '[visit-table-cell-multiselect]',
  templateUrl: './visit-table-cell-multiselect.component.html',
  styleUrls: ['./visit-table-cell-multiselect.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VisitTableCellMultiselectComponent implements OnDestroy {
  @Input('items') items: SelectOption[] = [];
  @Input('parentForm') parentForm: UntypedFormGroup;
  @Input('controlName') controlName: string;
  @Input('inputPlaceholder') inputPlaceholder: string;
  @Input('minWidth') minWidth: string;

  @Output('valueChanged') valueChanged = new EventEmitter();

  @ViewChild(MatSelect) matSelect: MatSelect;

  panelOpened = false;

  private cachedValue: any[];
  private cachedItems: SelectOption[];
  private cachedClosedItems: SelectOption[] = [];

  private valueChangedSubject = new Subject<any>();
  private valueChangedSubscription: Subscription;

  constructor() {
    this.valueChangedSubscription = this.valueChangedSubject.pipe(
      debounceTime(2000)
    ).subscribe(value => {
      this.valueChanged.emit(value);
    });
  }

  // Render only the selected options while the panel is closed. mat-option components are
  // instantiated eagerly even when the panel never opens, so rendering full option lists in
  // every cell of a table multiplies into thousands of live components (rows x options).
  get visibleItems(): SelectOption[]
  {
    if (this.panelOpened)
    {
      return this.items;
    }

    const value: any[] = this.parentForm.get(this.controlName).value || [];
    if (value !== this.cachedValue || this.items !== this.cachedItems)
    {
      this.cachedValue = value;
      this.cachedItems = this.items;
      this.cachedClosedItems = this.items.filter(i => value.includes(i.value));
    }

    return this.cachedClosedItems;
  }

  onOpenedChange(opened: boolean): void
  {
    this.panelOpened = opened;
  }

  // openedChange fires only after the open animation completes, which is too late — the panel
  // would briefly show just the selected options. Expanding on mousedown/keydown makes the full
  // list available before the panel renders.
  onTriggerInteraction(): void
  {
    this.panelOpened = true;
  }

  // Closed-panel keyboard interaction (arrow keys, type-ahead) can change the value without ever
  // opening the panel, so openedChange(false) never fires to reset panelOpened. Falling back to
  // blur catches that case without racing the open animation the way a timed resync would.
  onBlur(): void
  {
    if (!this.matSelect?.panelOpen)
    {
      this.panelOpened = false;
    }
  }

  onValueChanged(event: any) {
    this.valueChangedSubject.next(event);
  }

  ngOnDestroy() {
    this.valueChangedSubscription.unsubscribe();
  }

}
